/**
 * IconSelectorModal.tsx - Icon Selection Modal for Builder
 * 
 * This component provides a comprehensive icon selection interface for
 * choosing icons throughout the fundraiser builder with search and categorization.
 * 
 * Key Features:
 * - Comprehensive Lucide icon library integration
 * - Search functionality with real-time filtering
 * - Icon categorization and organization
 * - Preview of selected icon in context
 * - Responsive grid layout for icon display
 * - Click-to-select with visual feedback
 * 
 * Icon Categories:
 * - General: Common icons for various uses
 * - Impact: Icons related to social impact and causes
 * - Finance: Money, donation, and financial icons
 * - People: User, community, and social icons
 * - Nature: Environmental and nature-related icons
 * 
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - onIconSelect: (iconName: string) => void - Icon selection handler
 * - selectedIcon?: string - Currently selected icon name
 * - category?: string - Initial category filter
 * 
 * Dependencies: Lucide icons library, portal rendering, search functionality
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X, Search } from 'lucide-react';
import * as Icons from 'lucide-react';

interface IconSelectorModalProps {
  visible: boolean;
  selectedIcon: string;
  onClose: () => void;
  onSelect: (iconName: string) => void;
  triggerRef?: React.RefObject<HTMLElement>;
}

const Overlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 999999;
  backdrop-filter: blur(4px);
  padding: 20px;
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 500px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  z-index: 1000000;
  
  @media (max-width: 768px) {
    max-width: 90vw;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 200px;
  
  @media (max-width: 480px) {
    width: 150px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: #ffffff;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  width: 16px;
  height: 16px;
  pointer-events: none;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 12px;
  flex-shrink: 0;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 23px 8px;
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  max-height: 320px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(6, 1fr);
    padding: 16px 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    padding: 12px 16px;
  }
`;

const IconButton = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  background: ${props => props.selected ? '#f0f9ff' : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    color: ${props => props.selected ? '#3b82f6' : '#64748b'};
    transition: color 0.2s ease;
    width: 20px;
    height: 20px;
  }

  &:hover svg {
    color: #3b82f6;
  }
`;

const IconName = styled.div`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 6px;
  border-radius: 4px;
  backdrop-filter: blur(4px);

  ${IconButton}:hover & {
    opacity: 1;
  }
`;

const NoResults = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #64748b;
  text-align: center;
  flex: 1;
`;

const NoResultsIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 24px;
`;

// Comprehensive list of available icons organized by fundraiser categories
const availableIcons = [
  // Core Fundraising & Donations
  'Heart', 'Handshake', 'Gift', 'DollarSign', 'TrendingUp', 'Target',
  'CreditCard', 'CircleDollarSign', 'Coins', 'Banknote',
  
  // People & Community
  'Users', 'User', 'UserPlus', 'UserCheck', 'Baby', 'Crown', 
  'Smile', 'Cake', 'Sparkles', 'PartyPopper',
  
  // Health & Medical
  'Stethoscope', 'Pill', 'Cross', 'Activity', 'Zap', 'Shield', 
  'ShieldCheck', 'Plus', 'HeartHandshake',
  
  // Education & Learning
  'GraduationCap', 'BookOpen', 'Book', 'School', 'PenTool',
  'Pencil', 'Calculator', 'Lightbulb', 'Brain',
  
  // Animals & Pets
  'Dog', 'Cat', 'Bird', 'Fish', 'Rabbit', 'Bug', 'PawPrint',
  
  // Environment & Nature
  'TreePine', 'Trees', 'Leaf', 'Flower', 'Sprout', 'Sun', 'Cloud',
  'Droplets', 'Waves', 'Mountain', 'Globe', 'Recycle', 'Wind', 'Flame',
  
  // Sports & Recreation
  'Trophy', 'Medal', 'Award', 'Star', 'Flag', 'Football', 'Dumbbell',
  'Bike', 'Gamepad2', 'Music', 'Guitar', 'Palette', 'Camera',
  
  // Housing & Shelter
  'Home', 'Building', 'Building2', 'Church', 'Warehouse', 'Tent',
  'Bed', 'Key', 'Hammer', 'Wrench', 'HardHat',
  
  // Food & Nutrition
  'Utensils', 'ChefHat', 'Apple', 'Carrot', 'Wheat', 'Coffee',
  'Pizza', 'Cookie', 'IceCream', 'UtensilsCrossed',
  
  // Emergency & Crisis
  'AlertTriangle', 'Siren', 'ShieldAlert', 'Flame', 'Umbrella', 
  'LifeBuoy', 'Truck', 'Plane', 'Phone', 'Radio',
  
  // Arts & Culture
  'Palette', 'Paintbrush', 'Music', 'Mic', 'Camera', 'Video',
  'Brush', 'Scissors', 'Sparkles', 'Wand2',
  
  // Technology & Innovation
  'Lightbulb', 'Rocket', 'Laptop', 'Smartphone', 'Wifi', 'Settings',
  'Cog', 'Cpu', 'Database', 'Code',
  
  // Travel & Transportation
  'Plane', 'Car', 'Bus', 'Train', 'Bike', 'Ship', 'MapPin',
  'Navigation', 'Compass', 'Map', 'Route',
  
  // Communication & Outreach
  'MessageCircle', 'Mail', 'Send', 'Bell', 'Megaphone', 'Radio',
  'Newspaper', 'Share', 'Link', 'Share2',
  
  // Time & Events
  'Calendar', 'Clock', 'Timer', 'Hourglass', 'CalendarDays',
  'CalendarCheck', 'Watch', 'AlarmClock',
  
  // General Purpose
  'CheckCircle', 'XCircle', 'Info', 'HelpCircle', 'Search', 'Filter',
  'Eye', 'EyeOff', 'Lock', 'Unlock', 'Download', 'Upload', 'Bookmark',
  'Tag', 'Tags', 'Briefcase', 'ShoppingBag', 'ShoppingCart'
];

export const IconSelectorModal: React.FC<IconSelectorModalProps> = ({
  visible,
  selectedIcon,
  onClose,
  onSelect,
  triggerRef
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Handle escape key and body scroll lock
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [visible, onClose]);

  const filteredIcons = availableIcons.filter(iconName =>
    iconName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleIconSelect = (iconName: string) => {
    onSelect(iconName);
    onClose();
  };

  const renderIcon = (iconName: string) => {
    const Icon = (Icons as any)[iconName];
    return Icon ? <Icon /> : <Icons.Heart />;
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (!visible) return null;

  // Use portal to render modal at document body level
  return createPortal(
    <Overlay visible={visible} onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Select Icon</ModalTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <SearchContainer>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Search icons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748b',
                    padding: '2px'
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </SearchContainer>
            <CloseButton onClick={onClose}>
              <X size={18} />
            </CloseButton>
          </div>
        </ModalHeader>

        {filteredIcons.length === 0 ? (
          <NoResults>
            <NoResultsIcon>🔍</NoResultsIcon>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
              No icons found
            </h4>
            <p style={{ margin: '0', lineHeight: '1.5', fontSize: '14px' }}>
              Try searching with different keywords
            </p>
          </NoResults>
        ) : (
          <IconGrid>
            {filteredIcons.map(iconName => (
              <IconButton
                key={iconName}
                selected={selectedIcon === iconName}
                onClick={() => handleIconSelect(iconName)}
                title={iconName}
              >
                {renderIcon(iconName)}
                <IconName>{iconName}</IconName>
              </IconButton>
            ))}
          </IconGrid>
        )}
      </ModalContent>
    </Overlay>,
    document.body
  );
};